import React, { useState, useEffect } from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  TextField,
  Button,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Fade,
  Popover,
  List,
  ListItem,
  Divider
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  FileDownload as DownloadIcon,
  History as HistoryIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Build as ServiceIcon,
  DragIndicator as DragIcon,
  Event as EventIcon,
  VpnKey as KeyIcon
} from '@mui/icons-material';
import useAppStore from '../store/useAppStore';
import { scheduleAPI, serviceAPI } from '../services/apiWithAuth';
import { useMsal } from '@azure/msal-react';
import CellEditor from './CellEditor';
import WorkTypeSelector from './WorkTypeSelector';
import ChangeLogDialog from './ChangeLogDialog';
import GeneralHistoryDialog from './GeneralHistoryDialog';
import ServiceAssignDialog from './ServiceAssignDialog';
import HolidayManager from './HolidayManager';
import { useAuth } from '../hooks/useAuth';

const WorkCalendar = () => {
  const isAuthenticated = useIsAuthenticated();
  const { accounts } = useMsal();
  const { getUserRole, hasRole } = useAuth();
  const { currentEmployee } = useAppStore();
  
  // Helper to get current user email
  const getCurrentUserEmail = () => {
    if (accounts && accounts.length > 0) {
      return accounts[0].username || accounts[0].email || 'user';
    }
    return 'user';
  };
  
  // Sprawdza czy użytkownik może edytować daną komórkę
  const canEditCell = (employeeCode) => {
    // Administrator może edytować wszystko
    if (hasRole('Administrator')) {
      return true;
    }
    
    // Pracownik/Serwisant może edytować tylko swoje wpisy
    if (currentEmployee && currentEmployee.code === employeeCode) {
      return true;
    }
    
    return false;
  };
  
  const {
    employees,
    workTypes,
    scheduleData,
    selectedDate,
    activeWorkType,
    quickInputMode,
    services,
    selectionMode,
    selectedCells,
    holidayMode,
    employeeOrderLoaded,
    loadEmployeeOrder,
    getSortedEmployees,
    moveEmployee,
    setSelectedDate,
    setScheduleData,
    setServices,
    setNotification,
    getServiceForDate,
    getTodaysService,
    setActiveWorkType,
    updateScheduleCell,
    isCellSelected,
    isCellCut,
    toggleCellSelection,
    selectCellRange,
    clearSelection,
    addHoliday,
    isHoliday,
    getHoliday
  } = useAppStore();

  const [loading, setLoading] = useState(false);
  const [editingCell, setEditingCell] = useState(null);

  // Highlight state for row/column focus
  const [highlightedEmployee, setHighlightedEmployee] = useState(null);
  const [highlightedDay, setHighlightedDay] = useState(null);

  // Drag and drop state
  const [draggedEmployee, setDraggedEmployee] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // Context menu state
  const [contextMenu, setContextMenu] = useState(null);
  const [contextMenuData, setContextMenuData] = useState(null);
  
  // ChangeLog dialog state
  const [changeLogDialog, setChangeLogDialog] = useState(false);
  const [changeLogData, setChangeLogData] = useState(null);
  
  // General history dialog state
  const [generalHistoryDialog, setGeneralHistoryDialog] = useState(false);
  
  // Holiday manager dialog state
  const [holidayManagerDialog, setHolidayManagerDialog] = useState(false);
  
  // Service assign dialog state
  const [serviceAssignDialog, setServiceAssignDialog] = useState(false);
  const [serviceAssignData, setServiceAssignData] = useState(null);
  
  // Hours editor state
  const [editingHours, setEditingHours] = useState(null);
  const [startTime, setStartTime] = useState('08:00');
  const [endTime, setEndTime] = useState('16:00');

  // Selection state for range selection
  const [selectionStart, setSelectionStart] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);
  
  // Day summary popover state
  const [daySummaryAnchor, setDaySummaryAnchor] = useState(null);
  const [daySummaryDay, setDaySummaryDay] = useState(null);
  const [clickTimeout, setClickTimeout] = useState(null);

  // Generate calendar for current month
  const generateCalendarDays = () => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days = [];

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dayOfWeek = date.getDay(); // 0 = Sunday, 6 = Saturday
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      const isHolidayDay = isHoliday(year, month + 1, day);
      
      days.push({
        day,
        date,
        isWeekend,
        isHoliday: isHolidayDay,
        dayName: date.toLocaleDateString('pl-PL', { weekday: 'short' })
      });
    }
    return days;
  };

  // Handle clicking on day header (for adding holidays OR showing day summary)
  const handleDayHeaderClick = (e, day) => {
    // W trybie świąt - obsługa dodawania/usuwania świąt
    if (holidayMode) {
      const year = selectedDate.getFullYear();
      const month = selectedDate.getMonth() + 1;
      const existingHoliday = getHoliday(year, month, day);
      
      if (existingHoliday) {
        // Remove holiday
        addHoliday(year, month, day); // This will remove it since it exists
        setNotification({
          type: 'success',
          message: `Usunięto święto z dnia ${day}.${month.toString().padStart(2, '0')}.${year}`
        });
      } else {
        // Add holiday
        addHoliday(year, month, day, 'Święto');
        setNotification({
          type: 'success',
          message: `Dodano święto na dzień ${day}.${month.toString().padStart(2, '0')}.${year}`
        });
      }
      return;
    }
    
    // W normalnym trybie - opóźnij pojedyncze kliknięcie aby sprawdzić czy to nie double click
    if (clickTimeout) {
      clearTimeout(clickTimeout);
      setClickTimeout(null);
    }
    
    const targetElement = e.currentTarget; // Zapisz element przed timeoutem
    const timeout = setTimeout(() => {
      // Pokaż podsumowanie dnia (Del, W, N)
      setDaySummaryAnchor(targetElement);
      setDaySummaryDay(day);
      setClickTimeout(null);
    }, 250); // 250ms opóźnienie
    
    setClickTimeout(timeout);
  };

  // Get current week services
  const calendarDays = generateCalendarDays();
  
  // Handle double click on day header to highlight column
  const handleDayHeaderDoubleClick = (day) => {
    if (holidayMode) return; // Nie działaj w trybie świąt
    
    // Anuluj timeout pojedynczego kliknięcia
    if (clickTimeout) {
      clearTimeout(clickTimeout);
      setClickTimeout(null);
    }
    
    // Podświetlenie kolumny
    if (highlightedDay === day && highlightedEmployee === null) {
      setHighlightedDay(null);
      setNotification({
        type: 'info',
        message: 'Wyłączono podświetlenie'
      });
    } else {
      setHighlightedDay(day);
      setHighlightedEmployee(null);
      setNotification({
        type: 'info',
        message: `Podświetlono dzień: ${day} (ESC aby wyłączyć)`
      });
    }
  };
  
  const handleDaySummaryClose = () => {
    setDaySummaryAnchor(null);
    setDaySummaryDay(null);
  };
  
  // Get day summary data
  const getDaySummaryData = (day) => {
    if (!day || !scheduleData?.employees) return { delegacja: [], wolne: [], niedostepny: [] };
    
    const delegacja = [];
    const wolne = [];
    const niedostepny = [];
    
    // Przejdź przez wszystkich pracowników (wiersze) dla danego dnia (kolumna)
    getSortedEmployees().forEach(employee => {
      const cellData = getCellData(employee.code, day);
      
      if (cellData) {
        // Sprawdź różne formaty klucza dla kodu typu pracy
        const workTypeCode = cellData.work_type_code || cellData.work_type;
        
        if (workTypeCode) {
          const workType = workTypes.find(wt => wt.code === workTypeCode);
          
          if (workType) {
            const employeeInfo = {
              code: employee.code,
              name: employee.name,
              workType: workType
            };
            
            const codeUpper = workType.code.toUpperCase();
            const labelLower = workType.label?.toLowerCase() || '';
            
            // Kategoryzacja według typu pracy - sprawdź kod dokładnie
            if (codeUpper === 'DEL' || codeUpper === 'D' || codeUpper.includes('DEL') || labelLower.includes('delegacja')) {
              delegacja.push(employeeInfo);
            } else if (codeUpper === 'W' || labelLower.includes('wolne') || labelLower.includes('urlop')) {
              wolne.push(employeeInfo);
            } else if (codeUpper === 'N' || labelLower.includes('niedostępn')) {
              niedostepny.push(employeeInfo);
            }
          }
        }
      }
    });
    
    return { delegacja, wolne, niedostepny };
  };

  // Get employees with keys for a specific day (only those with "B" - Biuro)
  const getEmployeesWithKeysForDay = (day) => {
    if (!day || !scheduleData?.employees) return [];
    
    const employeesWithKeys = [];
    
    // Przejdź przez wszystkich pracowników dla danego dnia
    getSortedEmployees().forEach(employee => {
      // Sprawdź czy pracownik ma klucze
      if (employee.has_keys) {
        const cellData = getCellData(employee.code, day);
        
        if (cellData) {
          const workTypeCode = cellData.work_type_code || cellData.work_type;
          
          // Sprawdź czy ma wpisane "B" (Biuro)
          if (workTypeCode && workTypeCode.toUpperCase() === 'B') {
            employeesWithKeys.push({
              code: employee.code,
              name: employee.name
            });
          }
        }
      }
    });
    
    return employeesWithKeys;
  };

  // Load employee order on mount and when authentication status changes
  useEffect(() => {
    console.log('🔄 Loading employee order...', { isAuthenticated, employeeOrderLoaded });
    loadEmployeeOrder();
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load schedule data for current month
  useEffect(() => {
    loadMonthSchedule();
    loadMonthServices();
  }, [selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // ESC key handler to reset highlighting
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (highlightedEmployee !== null || highlightedDay !== null) {
          setHighlightedEmployee(null);
          setHighlightedDay(null);
          setNotification({
            type: 'info',
            message: 'Wyłączono podświetlenie'
          });
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [highlightedEmployee, highlightedDay, setNotification]);
  
  // Click outside calendar to reset highlighting
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Sprawdź czy kliknięto poza tabelą kalendarzową
      const tableElement = document.querySelector('[data-calendar-table]');
      if (tableElement && !tableElement.contains(event.target)) {
        if (highlightedEmployee !== null || highlightedDay !== null) {
          setHighlightedEmployee(null);
          setHighlightedDay(null);
        }
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [highlightedEmployee, highlightedDay]);

  const loadMonthSchedule = async () => {
    // Tylko przy pierwszym ładowaniu pokazuj loading
    const hasData = scheduleData?.employees?.length > 0;
    if (!hasData) {
      setLoading(true);
    }
    
    try {
      const year = selectedDate.getFullYear();
      const month = selectedDate.getMonth() + 1;
      
      const data = await scheduleAPI.getForMonth(year, month);
      // Upewnij się, że data ma poprawną strukturę
      if (data && typeof data === 'object') {
        setScheduleData(data);
      } else {
        // Fallback - pusta struktura
        setScheduleData({ employees: [] });
      }
    } catch (error) {
      console.error('Error loading schedule:', error);
      // W przypadku błędu, ustaw pustą strukturę
      setScheduleData({ employees: [] });
      setNotification({
        type: 'error',
        message: 'Błąd podczas ładowania kalendarza'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadMonthServices = async () => {
    try {
      const year = selectedDate.getFullYear();
      const month = selectedDate.getMonth() + 1;
      
      const servicesData = await serviceAPI.getForMonth(year, month);
      setServices(servicesData.services || []);
    } catch (error) {
      console.error('Error loading services:', error);
      setServices([]); // Reset na pustą listę w przypadku błędu
    }
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setSelectedDate(newDate);
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  const getCellData = (employeeCode, day) => {
    // Sprawdź czy scheduleData ma właściwość employees (format z backendu)
    if (scheduleData && scheduleData.employees) {
      const employee = scheduleData.employees.find(emp => emp.code === employeeCode);
      if (employee && employee.schedule) {
        return employee.schedule[day] || null;
      }
    }
    
    return null;
  };

  const getWorkTypeForCell = (employeeCode, day) => {
    const cellData = getCellData(employeeCode, day);
    if (cellData) {
      // Sprawdź różne formaty klucza
      const workTypeCode = cellData.work_type_code || cellData.work_type;
      return workTypes.find(wt => wt.code === workTypeCode);
    }
    
    // Jeśli nie ma danych w komórce, zwracamy null (pusta komórka)
    return null;
  };

  const handleCellDoubleClick = (employeeCode, day) => {
    const dayData = calendarDays.find(d => d.day === day);
    
    // Blokada dla sobót, niedziel i świąt
    if (dayData.isWeekend || dayData.isHoliday) {
      return;
    }
    
    // Podświetlenie krzyżowe - jeśli już podświetlone, resetuj; jeśli nie, podświetl
    if (highlightedEmployee === employeeCode && highlightedDay === day) {
      setHighlightedEmployee(null);
      setHighlightedDay(null);
      setNotification({
        type: 'info',
        message: 'Wyłączono podświetlenie'
      });
    } else {
      setHighlightedEmployee(employeeCode);
      setHighlightedDay(day);
      setNotification({
        type: 'info',
        message: `Podświetlono: ${employeeCode} / Dzień ${day} (ESC aby wyłączyć)`
      });
    }
  };

  const handleEmployeeClick = (employeeCode) => {
    // Toggle highlight dla wiersza pracownika
    if (highlightedEmployee === employeeCode && highlightedDay === null) {
      setHighlightedEmployee(null);
      setNotification({
        type: 'info',
        message: 'Wyłączono podświetlenie'
      });
    } else {
      setHighlightedEmployee(employeeCode);
      setHighlightedDay(null);
      setNotification({
        type: 'info',
        message: `Podświetlono pracownika: ${employeeCode} (ESC aby wyłączyć)`
      });
    }
  };

  const handleHoursSave = async () => {
    if (!editingHours) return;
    
    const { employeeCode, day, date } = editingHours;
    
    // Kombinuj czasy start i end
    const hoursValue = startTime && endTime ? `${startTime} - ${endTime}` : '';
    
    try {
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const dayNum = date.getDate();
      
      // Sprawdź czy istnieje już wpis dla tego dnia
      const cellData = getCellData(employeeCode, day);
      
      const scheduleEntry = {
        employee_code: employeeCode,
        year,
        month,
        day: dayNum,
        work_type_code: cellData?.work_type_code || cellData?.work_type || '',
        custom_text: cellData?.custom_text || '',
        hours: hoursValue,
        updated_by: getCurrentUserEmail()
      };

      if (cellData) {
        // Update existing entry
        await scheduleAPI.updateSchedule(scheduleEntry);
      } else {
        // Create new entry with hours only
        await scheduleAPI.createSchedule(scheduleEntry);
      }
      
      setNotification({
        type: 'success',
        message: 'Zapisano godziny pracy'
      });
      
      // Reload schedule data
      await loadMonthSchedule();
      
    } catch (error) {
      console.error('Error saving hours:', error);
      setNotification({
        type: 'error',
        message: 'Błąd podczas zapisywania godzin'
      });
    }
    
    setEditingHours(null);
    setStartTime('08:00');
    setEndTime('16:00');
  };

  const handleHoursCancel = () => {
    setEditingHours(null);
    setStartTime('08:00');
    setEndTime('16:00');
  };

  const formatHours = (hoursString) => {
    if (!hoursString) return '';
    
    // Sprawdź czy to już sformatowane godziny (HH:MM - HH:MM)
    if (hoursString.match(/^\d{2}:\d{2} - \d{2}:\d{2}$/)) {
      return hoursString;
    }
    
    // Sprawdź czy format to np "10 do 18" lub "10-18" (dla kompatybilności wstecznej)
    const oldMatch = hoursString.match(/(\d{1,2})\s*(?:do|-)\s*(\d{1,2})/);
    if (oldMatch) {
      const startHour = parseInt(oldMatch[1]);
      const endHour = parseInt(oldMatch[2]);
      
      if (startHour >= 0 && startHour <= 23 && endHour >= 0 && endHour <= 23) {
        return `${startHour.toString().padStart(2, '0')}:00 - ${endHour.toString().padStart(2, '0')}:00`;
      }
    }
    
    return '';
  };

  const handleCellClick = async (employeeCode, day, event = null) => {
    const cellData = getCellData(employeeCode, day);
    const dayData = calendarDays.find(d => d.day === day);
    
    // Sprawdź uprawnienia do edycji
    if (!canEditCell(employeeCode)) {
      setNotification({
        type: 'error',
        message: 'Nie masz uprawnień do edycji tego wpisu. Możesz edytować tylko swoje wpisy.'
      });
      return;
    }
    
    // Blokada edycji sobót, niedziel i świąt
    if (dayData.isWeekend || dayData.isHoliday) {
      if (!selectionMode) {
        const blockedType = dayData.isWeekend ? 'weekendy' : 'święta';
        setNotification({
          type: 'warning',
          message: `Nie można edytować harmonogramu w ${blockedType}`
        });
      }
      return;
    }

    // Selection mode handling
    if (selectionMode) {
      // Handle different types of selection
      if (event && event.shiftKey && selectionStart) {
        // Range selection with Shift
        selectCellRange(selectionStart.employeeCode, selectionStart.day, employeeCode, day);
      } else if (event && (event.ctrlKey || event.metaKey)) {
        // Toggle selection with Ctrl/Cmd
        toggleCellSelection(employeeCode, day);
      } else {
        // Normal click - start new selection or toggle single cell
        if (!event || (!event.ctrlKey && !event.metaKey)) {
          // Clear previous selection if not holding Ctrl
          clearSelection();
        }
        toggleCellSelection(employeeCode, day);
        setSelectionStart({ employeeCode, day });
      }
      return;
    }
    
    // Quick input mode - directly save the active work type
    if (quickInputMode && activeWorkType) {
      // Obsługa serwisu
      if (activeWorkType.code === 'service') {
        const year = dayData.date.getFullYear();
        const month = dayData.date.getMonth() + 1;
        const dayNum = dayData.date.getDate();
        
        // Sprawdź czy pracownik ma uprawnienia do serwisu
        const employee = employees.find(emp => emp.code === employeeCode);
        if (!employee || !employee.is_service) {
          setNotification({
            type: 'error',
            message: `Pracownik ${employeeCode} nie ma uprawnień do serwisu. Tylko pracownicy oznaczeni jako "Serwis" mogą być przypisani do obowiązków serwisowych.`
          });
          return;
        }
        
        const existingService = getServiceForDate(year, month, dayNum);
        
        try {
          const serviceData = {
            employee_code: employeeCode,
            year,
            month,
            day: dayNum,
            custom_text: 'serwis',
            updated_by: getCurrentUserEmail()
          };

          if (existingService) {
            if (existingService.employee_code === employeeCode) {
              // Usuń serwis jeśli kliknięto na tego samego pracownika
              await serviceAPI.deleteService(year, month, dayNum, getCurrentUserEmail());
              setNotification({
                type: 'success',
                message: `Usunięto serwis z ${employeeCode}`
              });
            } else {
              // Przenieś serwis na nowego pracownika
              const updateData = {
                employee_code: employeeCode,
                custom_text: 'serwis',
                updated_by: getCurrentUserEmail()
              };
              await serviceAPI.updateService(year, month, dayNum, updateData);
              setNotification({
                type: 'success',
                message: `Przeniesiono serwis z ${existingService.employee_code} na ${employeeCode}`
              });
            }
          } else {
            // Utwórz nowy serwis
            await serviceAPI.createService(serviceData);
            setNotification({
              type: 'success',
              message: `Przypisano serwis do ${employeeCode}`
            });
          }

          // Reload services data
          await loadMonthServices();
          
        } catch (error) {
          console.error('Error assigning service:', error);
          setNotification({
            type: 'error',
            message: error.response?.data?.detail || 'Błąd podczas przypisywania serwisu'
          });
        }
        return;
      }
      
      // Jeśli wybrany typ to "Puste" (pusty string), usuń wpis
      if (activeWorkType.code === '') {
        if (cellData) {
          // Optimistic update - od razu usuń z UI
          const updatedScheduleData = { ...scheduleData };
          const employee = updatedScheduleData.employees?.find(emp => emp.code === employeeCode);
          if (employee && employee.schedule) {
            delete employee.schedule[day];
          }
          setScheduleData(updatedScheduleData);

          // Usuń istniejący wpis - przekaż parametry bezpośrednio
          const year = dayData.date.getFullYear();
          const month = dayData.date.getMonth() + 1;
          const dayNum = dayData.date.getDate();
          
          try {
            await scheduleAPI.deleteSchedule(employeeCode, year, month, dayNum, getCurrentUserEmail());
            setNotification({
              type: 'success',
              message: 'Usunięto wpis z harmonogramu'
            });
          } catch (error) {
            console.error('Error deleting schedule:', error);
            // Rollback on error
            await loadMonthSchedule();
            setNotification({
              type: 'error',
              message: 'Błąd podczas usuwania wpisu'
            });
          }
        }
        // Jeśli nie ma wpisu, nic nie rób
        return;
      }
      
      // Optimistic update - od razu dodaj do UI
      const updatedScheduleData = { ...scheduleData };
      let employee = updatedScheduleData.employees?.find(emp => emp.code === employeeCode);
      if (!employee) {
        // Dodaj pracownika jeśli nie istnieje
        const emp = employees.find(e => e.code === employeeCode);
        if (emp) {
          employee = { code: emp.code, name: emp.name, schedule: {} };
          updatedScheduleData.employees = updatedScheduleData.employees || [];
          updatedScheduleData.employees.push(employee);
        }
      }
      if (employee) {
        employee.schedule = employee.schedule || {};
        employee.schedule[day] = {
          work_type: activeWorkType.code,
          custom_text: ''
        };
      }
      setScheduleData(updatedScheduleData);

      // Normalny tryb - dodaj/zaktualizuj wpis
      const quickCellData = {
        employee_code: employeeCode,
        date: dayData.date,
        work_type_code: activeWorkType.code,
        custom_text: '',
        existing: cellData
      };
      
      try {
        await handleCellSave(quickCellData, true); // skipReload = true dla quick mode
      } catch (error) {
        // Rollback on error
        await loadMonthSchedule();
      }
      return;
    }
    
    // Normal mode - open editor dialog
    setEditingCell({
      employee_code: employeeCode,
      day,
      date: dayData.date,
      isWeekend: dayData.isWeekend,
      existing: cellData
    });
  };

  const handleCellSave = async (cellData, skipReload = false) => {
    try {
      const year = cellData.date.getFullYear();
      const month = cellData.date.getMonth() + 1;
      const day = cellData.date.getDate();
      
      const scheduleEntry = {
        employee_code: cellData.employee_code,
        year,
        month,
        day,
        work_type_code: cellData.work_type_code,
        custom_text: cellData.custom_text || null,
        updated_by: getCurrentUserEmail()
      };

      if (cellData.existing) {
        // Update existing entry
        await scheduleAPI.updateSchedule(scheduleEntry);
      } else {
        // Create new entry
        await scheduleAPI.createSchedule(scheduleEntry);
      }

      // Update the store's schedule data and record undo/redo action
      const newCellData = {
        work_type_code: cellData.work_type_code,
        custom_text: cellData.custom_text || null,
        updated_by: getCurrentUserEmail()
      };
      
      updateScheduleCell(cellData.employee_code, day, newCellData);

      // Only show notification and reload if not in quick mode (skipReload = false)
      if (!skipReload) {
        setNotification({
          type: 'success',
          message: cellData.existing ? 'Zaktualizowano harmonogram' : 'Dodano nowy wpis do harmonogramu'
        });

        // Reload schedule data to show changes
        await loadMonthSchedule();
      }
      
    } catch (error) {
      console.error('Error saving schedule:', error);
      setNotification({
        type: 'error',
        message: 'Błąd podczas zapisywania harmonogramu'
      });
      throw error; // Re-throw for optimistic update rollback
    }
  };

  // Context menu handlers
  const handleCellRightClick = (event, employeeCode, day) => {
    event.preventDefault();
    
    // Sprawdź uprawnienia do edycji
    if (!canEditCell(employeeCode)) {
      setNotification({
        type: 'error',
        message: 'Nie masz uprawnień do edycji tego wpisu. Możesz edytować tylko swoje wpisy.'
      });
      return;
    }
    
    const dayData = calendarDays.find(d => d.day === day);
    if (dayData.isWeekend || dayData.isHoliday) {
      return; // Nie pokazuj context menu dla weekendów i świąt
    }

    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth() + 1;
    const cellData = getCellData(employeeCode, day);
    
    setContextMenuData({
      employeeCode,
      day,
      year,
      month,
      cellData,
      position: { x: event.clientX, y: event.clientY }
    });
    
    setContextMenu(event.currentTarget);
  };

  const handleContextMenuClose = () => {
    setContextMenu(null);
    setContextMenuData(null);
  };

  const handleShowHistory = () => {
    if (contextMenuData) {
      const { employeeCode, year, month, day, cellData } = contextMenuData;
      
      setChangeLogData({
        employeeCode,
        year,
        month,
        day,
        currentValue: cellData ? {
          work_type_code: cellData.work_type,
          custom_text: cellData.custom_text,
          updated_by: getCurrentUserEmail()
        } : null
      });
      
      setChangeLogDialog(true);
    }
    handleContextMenuClose();
  };

  const handleDeleteCell = async () => {
    if (contextMenuData) {
      const { employeeCode, year, month, day } = contextMenuData;
      
      // Sprawdź uprawnienia do edycji
      if (!canEditCell(employeeCode)) {
        setNotification({
          type: 'error',
          message: 'Nie masz uprawnień do usunięcia tego wpisu. Możesz edytować tylko swoje wpisy.'
        });
        handleContextMenuClose();
        return;
      }
      
      try {
        await scheduleAPI.deleteSchedule(employeeCode, year, month, day, getCurrentUserEmail());
        
        setNotification({
          type: 'success',
          message: 'Usunięto wpis z harmonogramu'
        });
        
        // Reload data
        await loadMonthSchedule();
        
      } catch (error) {
        console.error('Error deleting schedule cell:', error);
        setNotification({
          type: 'error',
          message: 'Błąd podczas usuwania wpisu'
        });
      }
    }
    handleContextMenuClose();
  };

  const handleEditCell = () => {
    if (contextMenuData) {
      const { employeeCode, day } = contextMenuData;
      
      // Sprawdź uprawnienia do edycji
      if (!canEditCell(employeeCode)) {
        setNotification({
          type: 'error',
          message: 'Nie masz uprawnień do edycji tego wpisu. Możesz edytować tylko swoje wpisy.'
        });
        handleContextMenuClose();
        return;
      }
      
      const dayData = calendarDays[day - 1];
      const cellData = getCellData(employeeCode, day);
      
      setEditingCell({
        employee_code: employeeCode,
        day,
        date: dayData.date,
        isWeekend: dayData.isWeekend,
        existing: cellData
      });
    }
    handleContextMenuClose();
  };

  const handleRestoreFromHistory = (restoreData) => {
    // Callback po przywróceniu wartości z historii
    console.log('Restored value:', restoreData);
    
    setNotification({
      type: 'success',
      message: `Przywrócono poprzednią wartość: ${restoreData.fieldName}`
    });
    
    // Reload data to show changes
    loadMonthSchedule();
  };

  const handleServiceAssign = async (serviceData) => {
    try {
      // Sprawdź czy pracownik ma uprawnienia do serwisu
      const employee = employees.find(emp => emp.code === serviceData.employee_code);
      if (!employee || !employee.is_service) {
        setNotification({
          type: 'error',
          message: `Pracownik ${serviceData.employee_code} nie ma uprawnień do serwisu. Tylko pracownicy oznaczeni jako "Serwis" mogą być przypisani do obowiązków serwisowych.`
        });
        return;
      }
      
      const existingService = getServiceForDate(serviceData.year, serviceData.month, serviceData.day);
      
      if (existingService) {
        // Update existing service
        await serviceAPI.updateService(serviceData.year, serviceData.month, serviceData.day, serviceData);
        setNotification({
          type: 'success',
          message: `Zmieniono serwis na ${serviceData.employee_code}`
        });
      } else {
        // Create new service
        await serviceAPI.createService(serviceData);
        setNotification({
          type: 'success',
          message: `Przypisano serwis do ${serviceData.employee_code} na ${serviceData.day}.${serviceData.month}.${serviceData.year}`
        });
      }
      
      // Reload services data
      await loadMonthServices();
      
    } catch (error) {
      console.error('Error assigning service:', error);
      
      if (error.response?.status === 409) {
        setNotification({
          type: 'error',
          message: 'Na ten dzień już jest przypisany serwis'
        });
      } else if (error.response?.status === 400 && error.response?.data?.detail) {
        // Wyświetl szczegółowy komunikat z backendu
        setNotification({
          type: 'error',
          message: error.response.data.detail
        });
      } else {
        setNotification({
          type: 'error',
          message: 'Błąd podczas przypisywania serwisu'
        });
      }
    }
  };

  const handleCellDelete = async (cellData) => {
    if (cellData.existing) {
      try {
        // Wyciągnij parametry z istniejącego wpisu
        const date = new Date(cellData.existing.work_date || cellData.existing.year + '-' + String(cellData.existing.month).padStart(2, '0') + '-' + String(cellData.existing.day).padStart(2, '0'));
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const day = date.getDate();
        
        await scheduleAPI.deleteSchedule(cellData.existing.employee_code, year, month, day, getCurrentUserEmail());
        
        // Update the store's schedule data and record undo/redo action
        updateScheduleCell(cellData.existing.employee_code, day, null);
        
        setNotification({
          type: 'success',
          message: 'Usunięto wpis z harmonogramu'
        });
        
        // Refresh data only once
        await loadMonthSchedule();
      } catch (error) {
        console.error('Error deleting schedule:', error);
        setNotification({
          type: 'error',
          message: 'Błąd podczas usuwania wpisu'
        });
      }
    }
  };

  const handleShowGeneralHistory = () => {
    setGeneralHistoryDialog(true);
  };

  const handleExportExcel = async () => {
    try {
      const year = selectedDate.getFullYear();
      const month = selectedDate.getMonth() + 1;
      
      const blob = await scheduleAPI.exportToExcel(year, month);
      
      // Utwórz link do pobrania
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `harmonogram_${year}_${month.toString().padStart(2, '0')}.xlsx`;
      
      // Automatyczne pobranie
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Zwolnij pamięć
      window.URL.revokeObjectURL(url);
      
      setNotification({
        type: 'success',
        message: 'Plik Excel został pobrany'
      });
      
    } catch (error) {
      console.error('Error exporting Excel:', error);
      setNotification({
        type: 'error',
        message: 'Błąd podczas eksportu do Excel'
      });
    }
  };

  // Funkcja do generowania inicjałów z imienia i nazwiska
  const getInitials = (name) => {
    if (!name) return '';
    return name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .join('');
  };

  // Funkcja sprawdzająca czy dzień to dzisiaj
  const isToday = (day) => {
    const today = new Date();
    const currentYear = selectedDate.getFullYear();
    const currentMonth = selectedDate.getMonth();
    
    return today.getFullYear() === currentYear && 
           today.getMonth() === currentMonth && 
           today.getDate() === day;
  };

  // Drag and drop handlers
  const handleDragStart = (e, employeeCode, index) => {
    setDraggedEmployee({ code: employeeCode, index });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedEmployee && draggedEmployee.index !== dropIndex) {
      moveEmployee(draggedEmployee.index, dropIndex);
      setNotification({
        type: 'success',
        message: 'Zmieniono kolejność pracowników'
      });
    }
    
    setDraggedEmployee(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedEmployee(null);
    setDragOverIndex(null);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 0.5, sm: 1 }, width: '100%', overflow: 'hidden' }} data-calendar-container>
      {/* Calendar Header */}
      <Paper sx={{ p: { xs: 0.25, sm: 0.75, md: 1 } }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: { xs: 0.5, sm: 1 }, 
          mb: { xs: 0.25, sm: 0.5, md: 1 }, 
          flexWrap: 'wrap',
          justifyContent: { xs: 'center', sm: 'flex-start' }
        }}>
          <IconButton onClick={() => navigateMonth(-1)} size="small" disabled={loading}>
            <ChevronLeftIcon />
          </IconButton>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: { xs: 150, sm: 200 } }}>
            <Typography 
              variant="h5" 
              sx={{ 
                textAlign: 'center',
                fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' }
              }}
            >
              {selectedDate.toLocaleDateString('pl-PL', { 
                month: 'long', 
                year: 'numeric' 
              })}
            </Typography>
            <Fade in={loading}>
              <CircularProgress size={16} sx={{ ml: 1 }} />
            </Fade>
          </Box>
          
          <IconButton onClick={() => navigateMonth(1)} size="small" disabled={loading}>
            <ChevronRightIcon />
          </IconButton>
          
          <Button
            variant="outlined"
            startIcon={<TodayIcon />}
            onClick={goToToday}
            size="small"
            sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
          >
            Dziś
          </Button>
          
          <IconButton 
            onClick={goToToday}
            size="small"
            sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
            title="Dziś"
          >
            <TodayIcon />
          </IconButton>
          
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExportExcel}
            size="small"
            color="secondary"
            sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
          >
            Excel
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<EventIcon />}
            onClick={() => setHolidayManagerDialog(true)}
            size="small"
            color="secondary"
            sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
          >
            Święta
          </Button>
          
          {/* Today's Service Info - inline */}
          <Box sx={{ 
            display: { xs: 'none', md: 'flex' }, 
            alignItems: 'center', 
            gap: 1,
            ml: 'auto',
            pl: 2
          }}>
            {(() => {
              const todaysService = getTodaysService();
              const todaysServiceEmployee = todaysService ? 
                employees.find(emp => emp.code === todaysService.employee_code) : null;
              
              return (
                <>
                  <Box sx={{
                    backgroundColor: '#d32f2f',
                    borderRadius: '50%',
                    width: 18,
                    height: 18,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    border: '1px solid white'
                  }}>
                    <Typography variant="caption" sx={{ 
                      fontSize: '9px',
                      color: 'white',
                      fontWeight: 'bold',
                      lineHeight: 1
                    }}>
                      S
                    </Typography>
                  </Box>
                  
                  <Typography variant="body1" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                    Serwis dziś:
                  </Typography>
                  
                  {todaysService ? (
                    <Chip
                      icon={
                        <Box sx={{
                          backgroundColor: 'white',
                          borderRadius: '50%',
                          width: 14,
                          height: 14,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Typography variant="caption" sx={{ 
                            fontSize: '7px',
                            color: '#d32f2f',
                            fontWeight: 'bold',
                            lineHeight: 1
                          }}>
                            S
                          </Typography>
                        </Box>
                      }
                      label={`${todaysService.employee_code} - ${todaysServiceEmployee?.name || 'Nieznany'}`}
                      color="primary"
                      variant="filled"
                      size="small"
                      sx={{ 
                        backgroundColor: '#d32f2f',
                        border: `2px solid #b71c1c`,
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      Brak
                    </Typography>
                  )}
                  
                  <Button
                    variant={quickInputMode && activeWorkType?.code === 'service' ? "contained" : "outlined"}
                    onClick={() => {
                      if (quickInputMode && activeWorkType?.code === 'service') {
                        setActiveWorkType(null);
                      } else {
                        setActiveWorkType({ code: 'service', label: 'Serwis', color: '#d32f2f' });
                      }
                    }}
                    size="small"
                    sx={{
                      backgroundColor: quickInputMode && activeWorkType?.code === 'service' ? '#d32f2f' : 'transparent',
                      borderColor: '#d32f2f',
                      color: quickInputMode && activeWorkType?.code === 'service' ? 'white' : '#d32f2f',
                      whiteSpace: 'nowrap',
                      '&:hover': {
                        backgroundColor: quickInputMode && activeWorkType?.code === 'service' ? '#b71c1c' : 'rgba(211, 47, 47, 0.1)',
                        borderColor: '#b71c1c'
                      }
                    }}
                  >
                    Przypisz serwis
                  </Button>
                </>
              );
            })()}
          </Box>
        </Box>

        {/* Work Type Selector for Quick Input - moved up */}
        <WorkTypeSelector />
        
        {/* Holiday Mode Indicator */}
        {holidayMode && (
          <Paper sx={{ p: 2, mt: 2, backgroundColor: 'warning.light', color: 'warning.contrastText' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Tryb dodawania świąt AKTYWNY
            </Typography>
            <Typography variant="body2">
              Kliknij na nagłówki dni w kalendarzu aby dodać lub usunąć święta. 
              Naciśnij <strong>Ctrl+H</strong> aby wyłączyć tryb świąt.
            </Typography>
          </Paper>
        )}
      </Paper>

      {/* Calendar Grid */}
      <Box sx={{ 
        width: '100%',
        maxHeight: 'calc(100vh - 350px)',
        overflowX: 'auto',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        // Czarne scrollbary poziome
        '&::-webkit-scrollbar': {
          height: '12px',
          width: '12px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'rgba(0, 0, 0, 0.1)',
          borderRadius: '6px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#000000',
          borderRadius: '6px',
          '&:hover': {
            backgroundColor: '#333333',
          },
        },
        // Czarny narożnik scrollbarów
        '&::-webkit-scrollbar-corner': {
          backgroundColor: 'rgba(0, 0, 0, 0.1)',
        },
      }}>
        <TableContainer component={Paper} sx={{ minWidth: { xs: 800, md: '100%' }, overflow: 'visible' }} data-calendar-table>
          <Table 
            size="medium" 
            stickyHeader
            sx={(theme) => ({
              '& .MuiTableCell-root': {
                borderRight: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[700] : '#bdbdbd'}`,
                minHeight: { xs: '60px', sm: '70px', md: '80px' },
                padding: { xs: '8px 4px', sm: '10px 6px', md: '12px 8px' },
                fontSize: { xs: '0.75rem', sm: '0.813rem', md: '0.875rem' }
              }
            })}
          >
          <TableHead>
            <TableRow>
              <TableCell sx={{ 
                fontWeight: 'bold', 
                minWidth: { xs: 100, sm: 120, md: 140 }, 
                width: { xs: 100, sm: 120, md: 140 },
                position: 'sticky',
                left: 0,
                top: 0,
                zIndex: 1000,
                backgroundColor: 'background.paper',
                boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
              }}>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontWeight: 'bold',
                    fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' }
                  }}
                >
                  Pracownik
                </Typography>
              </TableCell>
              {calendarDays.map(({ day, dayName, isWeekend, isHoliday: isDayHoliday }) => {
                const todayFlag = isToday(day);
                const holiday = getHoliday(selectedDate.getFullYear(), selectedDate.getMonth() + 1, day);
                
                // Sprawdź czy są pracownicy z kluczami dla tego dnia (B - Biuro)
                const employeesWithKeys = getEmployeesWithKeysForDay(day);
                const noKeysWarning = !isWeekend && !isDayHoliday && employeesWithKeys.length === 0;
                
                return (
                <TableCell
                  key={day}
                  align="center"
                  onClick={(e) => handleDayHeaderClick(e, day)}
                  onDoubleClick={() => handleDayHeaderDoubleClick(day)}
                  sx={(theme) => ({
                    fontWeight: 'bold',
                    minWidth: 60,
                    width: 60,
                    padding: '12px 6px',
                    cursor: holidayMode ? 'pointer' : (highlightedDay === day || highlightedDay === null ? 'pointer' : 'default'),
                    position: 'sticky',
                    top: 0,
                    zIndex: 100,
                    // PRIORYTET: Ostrzeżenie o braku kluczy (czerwony przezroczysty)
                    backgroundColor: noKeysWarning ?
                      (theme.palette.mode === 'dark' ? 'rgba(211, 47, 47, 0.3)' : 'rgba(244, 67, 54, 0.15)') :
                      isDayHoliday ? 
                        (theme.palette.mode === 'dark' ? '#8e24aa' : '#e1bee7') :
                        isWeekend ? 
                          (theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100]) : 
                          theme.palette.background.paper,
                    color: (isWeekend || isDayHoliday) ? 'text.secondary' : 'inherit',
                    // Border dla ostrzeżenia (używamy boxShadow inset żeby nie uciąć)
                    ...(noKeysWarning && {
                      boxShadow: 'inset 0 0 0 2px rgba(183, 28, 28, 0.5), 0 0 8px rgba(244, 67, 54, 0.3)',
                    }),
                    // Podświetlenie zaznaczonej kolumny
                    opacity: highlightedEmployee === null && highlightedDay !== null && highlightedDay !== day ? 0.3 : 1,
                    transition: 'opacity 0.3s ease, background-color 0.3s ease, transform 0.2s ease',
                    // Subtelne podświetlenie dzisiejszego dnia
                    ...(todayFlag && !noKeysWarning && {
                      backgroundColor: isDayHoliday ?
                        (theme.palette.mode === 'dark' ? '#7b1fa2' : '#d1c4e9') :
                        isWeekend ?
                          (theme.palette.mode === 'dark' ? 'grey.700' : '#f3e5f5') :
                          (theme.palette.mode === 'dark' ? 'grey.700' : '#f3e5f5'),
                      borderRadius: '8px'
                    }),
                    // Hover effect
                    ...(!holidayMode && highlightedEmployee === null && {
                      '&:hover': {
                        backgroundColor: noKeysWarning ?
                          (theme.palette.mode === 'dark' ? '#ef6c00' : '#fb8c00') :
                          (theme.palette.mode === 'dark' ? 'grey.700' : '#f3e5f5'),
                        transform: 'scale(1.02)'
                      }
                    }),
                    // Hover effect w trybie świąt
                    ...(holidayMode && {
                      '&:hover': {
                        backgroundColor: isDayHoliday ?
                          (theme.palette.mode === 'dark' ? '#6a1b9a' : '#ce93d8') :
                          (theme.palette.mode === 'dark' ? '#7b1fa2' : '#e1bee7'),
                        transform: 'scale(1.05)',
                        transition: 'all 0.2s ease'
                      }
                    })
                  })}
                  title={
                    noKeysWarning ?
                      'Brak osoby z kluczami do biura' :
                    holidayMode ? 
                      (isDayHoliday ? 
                        `Kliknij aby usunąć święto: ${holiday?.name || 'Święto'}` : 
                        'Kliknij aby dodać święto') :
                      (isDayHoliday ? 
                        `Święto: ${holiday?.name || 'Święto'}` : 
                        '')
                  }
                >
                  <Box>
                    <Typography variant="caption" display="block">
                      {dayName}
                    </Typography>
                    <Typography variant="body2" sx={(theme) => ({
                      ...(todayFlag ? {
                        backgroundColor: theme.palette.mode === 'dark' ? '#bb86fc' : '#9c27b0',
                        color: 'white',
                        borderRadius: '50%',
                        width: 24,
                        height: 24,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto',
                        fontWeight: 'bold'
                      } : {})
                    })}>
                      {day}
                    </Typography>
                  </Box>
                </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          
          <TableBody>
            {getSortedEmployees().map((employee, index) => (
              <TableRow 
                key={employee.code}
                draggable
                onDragStart={(e) => handleDragStart(e, employee.code, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
                sx={{
                  backgroundColor: dragOverIndex === index ? 'action.hover' : 'inherit',
                  opacity: (draggedEmployee?.index === index || 
                           (highlightedDay === null && highlightedEmployee !== null && highlightedEmployee !== employee.code)) ? 0.3 : 1,
                  cursor: 'grab',
                  '&:active': {
                    cursor: 'grabbing'
                  },
                  transition: 'opacity 0.3s ease, background-color 0.2s ease'
                }}
              >
                <TableCell 
                  onClick={() => handleEmployeeClick(employee.code)}
                  sx={{ 
                  fontWeight: 'bold', 
                  minWidth: { xs: 100, sm: 120, md: 140 }, 
                  width: { xs: 100, sm: 120, md: 140 }, 
                  padding: { xs: '12px 8px', sm: '14px 10px', md: '16px 12px' },
                  position: 'sticky',
                  left: 0,
                  zIndex: 10,
                  backgroundColor: 'background.paper',
                  boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
                  cursor: 'pointer',
                  transition: 'background-color 0.3s ease, transform 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    transform: 'scale(1.02)'
                  }
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
                    <DragIcon sx={{ 
                      color: 'text.secondary',
                      cursor: 'grab',
                      fontSize: { xs: '1rem', sm: '1.25rem' },
                      '&:active': { cursor: 'grabbing' }
                    }} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="h6" sx={{ 
                          fontSize: { xs: '1rem', sm: '1.15rem', md: '1.3rem' },
                          fontWeight: 'bold',
                          color: 'primary.main',
                          lineHeight: 1
                        }}>
                          {employee.code}
                        </Typography>
                        {employee.has_keys && (
                          <Tooltip title="Posiada klucze">
                            <KeyIcon sx={{ 
                              color: '#f9a825', 
                              fontSize: { xs: '0.9rem', sm: '1rem' },
                              ml: 0.3
                            }} />
                          </Tooltip>
                        )}
                      </Box>
                      <Typography variant="caption" sx={{ 
                        fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.8rem' },
                        color: 'text.secondary',
                        lineHeight: 1,
                        display: { xs: 'none', sm: 'block' },
                        marginTop: '2px'
                      }}>
                        {employee.name}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                
                {calendarDays.map(({ day, isWeekend, isHoliday: isDayHoliday }) => {
                  const workType = getWorkTypeForCell(employee.code, day);
                  const cellData = getCellData(employee.code, day);
                  
                  // Sprawdź czy jest serwis na ten dzień
                  const year = selectedDate.getFullYear();
                  const month = selectedDate.getMonth() + 1;
                  const hasService = getServiceForDate(year, month, day);
                  const isServiceEmployee = hasService && hasService.employee_code === employee.code;
                  
                  // Sprawdź czy to dzisiaj
                  const todayFlag = isToday(day);
                  
                  // Sprawdź czy komórka jest zaznaczona
                  const isSelected = isCellSelected(employee.code, day);
                  
                  // Sprawdź czy komórka jest wycięta
                  const isCut = isCellCut(employee.code, day);
                  
                  // Get holiday info if it's a holiday
                  const holiday = isDayHoliday ? getHoliday(year, month, day) : null;
                  
                  // Sprawdź czy można edytować (nie weekend i nie święto)
                  const isEditable = !isWeekend && !isDayHoliday;
                  
                  // Sprawdź czy komórka jest w podświetlonym wierszu lub kolumnie
                  const isInHighlightedRow = highlightedEmployee !== null && highlightedEmployee === employee.code;
                  const isInHighlightedColumn = highlightedDay !== null && highlightedDay === day;
                  const isInCrossHighlight = isInHighlightedRow || isInHighlightedColumn;
                  const shouldDim = (highlightedEmployee !== null || highlightedDay !== null) && !isInCrossHighlight;
                  
                  return (
                    <TableCell
                      key={day}
                      align="center"
                      sx={(theme) => ({
                        backgroundColor: workType?.color || 
                          (isDayHoliday ? 
                            (theme.palette.mode === 'dark' ? '#8e24aa' : '#e1bee7') :
                            isWeekend ? 
                              (theme.palette.mode === 'dark' ? 'grey.800' : '#e0e0e0') : 
                              (theme.palette.mode === 'dark' ? 'grey.900' : '#fafafa')
                          ),
                        cursor: !isEditable ? 'not-allowed' : (selectionMode ? 'crosshair' : 'pointer'),
                        position: 'relative',
                        minWidth: 60,
                        width: 60,
                        height: 80,
                        padding: '6px',
                        opacity: !isEditable ? 0.6 : (isCut ? 0.4 : (shouldDim ? 0.3 : 1)),
                        filter: isCut ? 'grayscale(0.7)' : 'none',
                        border: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[700] : '#bdbdbd'}`,
                        borderRadius: '4px',
                        transition: 'opacity 0.3s ease, background-color 0.2s ease, transform 0.2s ease',
                        // Subtelne podświetlenie dzisiejszego dnia
                        ...(todayFlag && {
                          backgroundColor: workType?.color ? 
                            `color-mix(in srgb, ${workType.color} 80%, ${theme.palette.mode === 'dark' ? '#424242' : '#f3e5f5'} 20%)` : 
                            (isDayHoliday ?
                              (theme.palette.mode === 'dark' ? '#7b1fa2' : '#d1c4e9') :
                              isWeekend ?
                                (theme.palette.mode === 'dark' ? 'grey.700' : '#f3e5f5') :
                                (theme.palette.mode === 'dark' ? 'grey.700' : '#f3e5f5')
                            )
                        }),
                        // Podświetlenie zaznaczonych komórek
                        ...(isSelected && {
                          boxShadow: `inset 0 0 0 3px ${theme.palette.primary.main}, 0 0 8px rgba(79, 70, 229, 0.3)`,
                          backgroundColor: workType?.color ? 
                            `color-mix(in srgb, ${workType.color} 70%, ${theme.palette.primary.light} 30%)` : 
                            (theme.palette.mode === 'dark' ? 'rgba(79, 70, 229, 0.3)' : 'rgba(79, 70, 229, 0.1)')
                        }),
                        // Box-shadow tylko dla serwisu
                        ...(isServiceEmployee && !isSelected && {
                          boxShadow: 'inset 0 0 0 2px #d32f2f, 0 0 8px rgba(211, 47, 47, 0.3)'
                        }),
                        // Kombinacja serwisu i zaznaczenia
                        ...(isServiceEmployee && isSelected && {
                          boxShadow: `inset 0 0 0 2px #d32f2f, inset 0 0 0 5px ${theme.palette.primary.main}, 0 0 8px rgba(211, 47, 47, 0.3), 0 0 12px rgba(79, 70, 229, 0.3)`
                        }),
                        '&:hover': isEditable ? {
                          backgroundColor: workType?.color ? 
                            `${workType.color}dd` : 
                            (theme.palette.mode === 'dark' ? 'grey.700' : '#e3f2fd'),
                          transform: selectionMode ? 'none' : 'scale(1.08)',
                          transition: 'all 0.2s ease-in-out',
                          boxShadow: isSelected ? 
                            `inset 0 0 0 3px ${theme.palette.primary.main}, 0 6px 20px ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.15)'}` :
                            `0 6px 20px ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.15)'}`,
                          zIndex: 2
                        } : {}
                      })}
                      onClick={(e) => isEditable && handleCellClick(employee.code, day, e)}
                      onDoubleClick={() => isEditable && handleCellDoubleClick(employee.code, day)}
                      onContextMenu={(e) => isEditable && handleCellRightClick(e, employee.code, day)}
                    >
                      <Tooltip 
                        title={
                          (() => {
                            // Pobierz pracowników z kluczami dla tego dnia
                            const employeesWithKeys = getEmployeesWithKeysForDay(day);
                            
                            // Podstawowy tooltip
                            let baseTooltip = '';
                            if (!isEditable) {
                              baseTooltip = isDayHoliday ? 
                                (holiday ? `${holiday.name}` : 'Święto') : 
                                'Weekend';
                            } else if (selectionMode) {
                              baseTooltip = isSelected ? 'Zaznaczona' : 'Kliknij aby zaznaczyć';
                            } else if (quickInputMode) {
                              baseTooltip = `${activeWorkType?.code} - ${activeWorkType?.label}`;
                            }
                            
                            // Dodaj informację o kluczach jeśli są pracownicy z kluczami
                            if (employeesWithKeys.length > 0) {
                              const keysInfo = `Klucze ma: ${employeesWithKeys.map(emp => emp.code).join(', ')}`;
                              return baseTooltip ? `${baseTooltip}\n${keysInfo}` : keysInfo;
                            }
                            
                            return baseTooltip;
                          })()
                        }
                      >
                        <Box sx={{ 
                          height: '100%', 
                          display: 'flex', 
                          flexDirection: 'column', 
                          justifyContent: 'center', 
                          alignItems: 'center',
                          overflow: 'hidden',
                          position: 'relative',
                          padding: '2px'
                        }}>
                          {/* Inicjały pracownika przy podświetlonej kolumnie */}
                          {highlightedDay === day && highlightedEmployee === null && (
                            <Box sx={{
                              position: 'absolute',
                              left: 2,
                              top: 2,
                              backgroundColor: 'primary.main',
                              color: 'white',
                              borderRadius: '4px',
                              padding: '2px 4px',
                              fontSize: '0.65rem',
                              fontWeight: 'bold',
                              lineHeight: 1,
                              zIndex: 10,
                              boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                            }}>
                              {employee.code}
                            </Box>
                          )}
                          
                          {/* Główna zawartość komórki */}
                          <Typography variant="body1" sx={{ 
                            fontWeight: 'bold', 
                            color: workType ? 'inherit' : (theme) => theme.palette.mode === 'dark' ? 'grey.500' : 'text.disabled',
                            fontSize: '0.9rem',
                            lineHeight: 1.2
                          }}>
                            {workType?.code || ''}
                          </Typography>
                          {cellData?.custom_text && (
                            <Tooltip title={cellData.custom_text} arrow placement="top">
                              <Typography variant="caption" sx={{ 
                                fontSize: '0.7rem',
                                lineHeight: 1.2,
                                textAlign: 'center',
                                overflow: 'hidden',
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                wordBreak: 'break-word',
                                maxWidth: '100%'
                              }}>
                                {cellData.custom_text}
                              </Typography>
                            </Tooltip>
                          )}
                          
                          {/* Wyświetlanie godzin pracy */}
                          {cellData?.hours && formatHours(cellData.hours) && (
                            <Tooltip title={formatHours(cellData.hours)} arrow placement="top">
                              <Typography variant="caption" sx={{ 
                                fontSize: '0.7rem',
                                lineHeight: 1.2,
                                textAlign: 'center',
                                overflow: 'hidden',
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                wordBreak: 'break-word',
                                maxWidth: '100%',
                                color: 'white',
                                fontWeight: 'bold',
                                textShadow: '1px 1px 2px rgba(0,0,0,0.7)',
                                marginTop: '2px'
                              }}>
                                {formatHours(cellData.hours)}
                              </Typography>
                            </Tooltip>
                          )}
                          
                          {/* Wskaźnik serwisu - elegancki badge */}
                          {isServiceEmployee && (
                            <Box sx={{
                              position: 'absolute',
                              top: 4,
                              right: 4,
                              backgroundColor: '#d32f2f',
                              borderRadius: '50%',
                              width: 14,
                              height: 14,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                              border: '1px solid white'
                            }}>
                              <Typography variant="caption" sx={{ 
                                fontSize: '8px',
                                color: 'white',
                                fontWeight: 'bold',
                                lineHeight: 1
                              }}>
                                S
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Tooltip>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      </Box>

      {/* Cell Editor Dialog */}
      {editingCell && (
        <CellEditor
          open={!!editingCell}
          cellData={editingCell}
          onClose={() => setEditingCell(null)}
          onSave={handleCellSave}
          onDelete={handleCellDelete}
        />
      )}

      {/* Context Menu */}
      <Menu
        open={Boolean(contextMenu)}
        onClose={handleContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenuData?.position ? {
            top: contextMenuData.position.y,
            left: contextMenuData.position.x
          } : undefined
        }
      >
        <MenuItem onClick={handleEditCell}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edytuj komórkę</ListItemText>
        </MenuItem>
        
        {contextMenuData?.cellData && (
          <MenuItem onClick={handleDeleteCell}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Usuń wpis</ListItemText>
          </MenuItem>
        )}
        
        <MenuItem onClick={handleShowHistory}>
          <ListItemIcon>
            <HistoryIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Historia zmian</ListItemText>
        </MenuItem>
      </Menu>

      {/* ChangeLog Dialog */}
      {changeLogData && (
        <ChangeLogDialog
          open={changeLogDialog}
          onClose={() => {
            setChangeLogDialog(false);
            setChangeLogData(null);
          }}
          employeeCode={changeLogData.employeeCode}
          year={changeLogData.year}
          month={changeLogData.month}
          day={changeLogData.day}
          currentValue={changeLogData.currentValue}
          onRestore={handleRestoreFromHistory}
        />
      )}

      {/* General History Dialog */}
      <GeneralHistoryDialog
        open={generalHistoryDialog}
        onClose={() => setGeneralHistoryDialog(false)}
        workTypes={workTypes}
      />

      {/* Service Assign Dialog */}
      {serviceAssignData && (
        <ServiceAssignDialog
          open={serviceAssignDialog}
          onClose={() => {
            setServiceAssignDialog(false);
            setServiceAssignData(null);
          }}
          onAssign={handleServiceAssign}
          employees={employees}
          selectedDate={serviceAssignData.selectedDate}
          existingService={serviceAssignData.existingService}
        />
      )}

      {/* Holiday Manager Dialog */}
      <HolidayManager
        open={holidayManagerDialog}
        onClose={() => setHolidayManagerDialog(false)}
      />

      {/* Hours Editor Overlay */}
      {editingHours && (
        <Box sx={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'background.paper',
          border: '2px solid #000',
          boxShadow: 24,
          p: 4,
          borderRadius: 1,
          zIndex: 1300,
          minWidth: 320
        }}>
          <Typography variant="h6" component="h2" gutterBottom>
            Edytuj godziny pracy
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', justifyContent: 'center' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Od
              </Typography>
              <TextField
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                inputProps={{
                  step: 300, // 5 min steps
                  onKeyDown: (e) => {
                    if (e.key === 'Tab' || e.key === 'Enter') {
                      e.preventDefault();
                      document.querySelector('input[type="time"]:last-of-type').focus();
                    }
                  }
                }}
                size="small"
                sx={{ 
                  width: 120,
                  '& input': {
                    textAlign: 'center',
                    fontSize: '1.1rem',
                    fontWeight: 'bold'
                  }
                }}
                autoFocus
              />
            </Box>
            <Typography variant="h5" sx={{ mt: 3, color: 'primary.main', fontWeight: 'bold' }}>
              -
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Do
              </Typography>
              <TextField
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                inputProps={{
                  step: 300, // 5 min steps
                  onKeyDown: (e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleHoursSave();
                    }
                  }
                }}
                size="small"
                sx={{ 
                  width: 120,
                  '& input': {
                    textAlign: 'center',
                    fontSize: '1.1rem',
                    fontWeight: 'bold'
                  }
                }}
              />
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
            Przykład: {startTime} - {endTime}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
            <Button onClick={handleHoursCancel} size="small" variant="outlined">
              Anuluj
            </Button>
            <Button onClick={handleHoursSave} variant="contained" size="small">
              Zapisz
            </Button>
          </Box>
        </Box>
      )}
      
      {/* Day Summary Popover */}
      <Popover
        open={Boolean(daySummaryAnchor)}
        anchorEl={daySummaryAnchor}
        onClose={handleDaySummaryClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            minWidth: 280,
            maxWidth: 400,
            boxShadow: 3
          }
        }}
      >
        {daySummaryDay && (() => {
          const summaryData = getDaySummaryData(daySummaryDay);
          const hasAnyData = summaryData.delegacja.length > 0 || 
                             summaryData.wolne.length > 0 || 
                             summaryData.niedostepny.length > 0;
          
          // Pobierz dzień tygodnia
          const dayDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), daySummaryDay);
          const dayName = dayDate.toLocaleDateString('pl-PL', { weekday: 'long' });
          const dayNameCapitalized = dayName.charAt(0).toUpperCase() + dayName.slice(1);
          
          return (
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                {dayNameCapitalized}, {daySummaryDay}.{(selectedDate.getMonth() + 1).toString().padStart(2, '0')}.{selectedDate.getFullYear()}
              </Typography>
              
              {!hasAnyData && (
                <Typography variant="body2" color="text.secondary">
                  Brak pracowników w delegacji, z wolnym lub niedostępnych.
                </Typography>
              )}
              
              {/* Sekcja z kluczami */}
              {(() => {
                const employeesWithKeys = getEmployeesWithKeysForDay(daySummaryDay);
                return (
                  <>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: employeesWithKeys.length > 0 ? '#388e3c' : '#d32f2f', mb: 1 }}>
                      🔑 Klucze do biura
                    </Typography>
                    {employeesWithKeys.length > 0 ? (
                      <List dense sx={{ mb: 1 }}>
                        {employeesWithKeys.map((emp) => (
                          <ListItem key={emp.code} sx={{ py: 0.5 }}>
                            <Chip
                              size="small"
                              label={emp.code}
                              sx={{
                                backgroundColor: '#f9a825',
                                color: 'white',
                                minWidth: 40,
                                mr: 1
                              }}
                            />
                            <Typography variant="body2">{emp.name}</Typography>
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                        Brak osoby z kluczami do biura
                      </Typography>
                    )}
                    {hasAnyData && <Divider sx={{ my: 1 }} />}
                  </>
                );
              })()}
              
              {summaryData.delegacja.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#ff9800', mb: 1 }}>
                    Delegacja ({summaryData.delegacja.length})
                  </Typography>
                  <List dense sx={{ mb: 1 }}>
                    {summaryData.delegacja.map((emp) => (
                      <ListItem key={emp.code} sx={{ py: 0.5 }}>
                        <Chip
                          size="small"
                          label={emp.code}
                          sx={{
                            backgroundColor: emp.workType.color,
                            color: 'white',
                            minWidth: 40,
                            mr: 1
                          }}
                        />
                        <Typography variant="body2">{emp.name}</Typography>
                      </ListItem>
                    ))}
                  </List>
                  {(summaryData.wolne.length > 0 || summaryData.niedostepny.length > 0) && <Divider sx={{ my: 1 }} />}
                </>
              )}
              
              {summaryData.wolne.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#fbc02d', mb: 1 }}>
                    Wolne / Urlop ({summaryData.wolne.length})
                  </Typography>
                  <List dense sx={{ mb: 1 }}>
                    {summaryData.wolne.map((emp) => (
                      <ListItem key={emp.code} sx={{ py: 0.5 }}>
                        <Chip
                          size="small"
                          label={emp.code}
                          sx={{
                            backgroundColor: emp.workType.color,
                            color: 'white',
                            minWidth: 40,
                            mr: 1
                          }}
                        />
                        <Typography variant="body2">{emp.name}</Typography>
                      </ListItem>
                    ))}
                  </List>
                  {summaryData.niedostepny.length > 0 && <Divider sx={{ my: 1 }} />}
                </>
              )}
              
              {summaryData.niedostepny.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#757575', mb: 1 }}>
                    Niedostępny ({summaryData.niedostepny.length})
                  </Typography>
                  <List dense>
                    {summaryData.niedostepny.map((emp) => (
                      <ListItem key={emp.code} sx={{ py: 0.5 }}>
                        <Chip
                          size="small"
                          label={emp.code}
                          sx={{
                            backgroundColor: emp.workType.color,
                            color: 'white',
                            minWidth: 40,
                            mr: 1
                          }}
                        />
                        <Typography variant="body2">{emp.name}</Typography>
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </Box>
          );
        })()}
      </Popover>
    </Box>
  );
};

export default WorkCalendar;